/**
 * 桌面壳(Tauri)桥接 —— 仅打包成 Tauri 应用时可用(tauri.conf `withGlobalTauri: true`
 * 暴露 `window.__TAURI__`)。浏览器 / vite dev 下 `isTauri()` = false,相关 UI 自动隐藏。
 *
 * 目前只用于:档口填自己的 DeepSeek key(智能客服 + 电子宠物的 iris sidecar 用;
 * 见 src-tauri/src/lib.rs 的 set_ai_key / ai_key_is_set 命令)。
 */
type Invoke = (cmd: string, args?: Record<string, unknown>) => Promise<unknown>

function invoker(): Invoke | null {
  const w = window as unknown as { __TAURI__?: { core?: { invoke?: Invoke } } }
  return w.__TAURI__?.core?.invoke ?? null
}

/** 当前是否跑在 Tauri 桌面壳里(决定要不要露出桌面专属 UI)。 */
export function isTauri(): boolean {
  return invoker() !== null
}

/** 在系统文件管理器打开本地图片文件夹(AI 生图等本地资产存放处)。仅桌面端可用。 */
export async function openMediaFolder(): Promise<void> {
  const invoke = invoker()
  if (!invoke) throw new Error('仅桌面端可用')
  await invoke('open_media_folder')
}

/** 「另存为」弹窗:用户选路径与文件名;取消返回 null。须在用户点击保存时同步调用。 */
export async function promptSaveFile(defaultName: string, ext: string): Promise<string | null> {
  const invoke = invoker()
  if (!invoke) throw new Error('仅桌面端可用')
  return (await invoke('prompt_save_file', { defaultName, ext })) as string | null
}

/** 将字节写入已选定的完整路径(Tauri)。 */
export async function writeFileBytes(path: string, data: Uint8Array): Promise<void> {
  const invoke = invoker()
  if (!invoke) throw new Error('仅桌面端可用')
  await invoke('write_file_bytes', { path, data })
}

/** 选文件夹保存二进制文件;用户取消返回 null;成功返回完整文件路径。 */
export async function saveFileToFolder(
  defaultName: string,
  ext: string,
  data: Uint8Array
): Promise<string | null> {
  const invoke = invoker()
  if (!invoke) throw new Error('仅桌面端可用')
  return (await invoke('save_file_to_folder', { defaultName, ext, data })) as string | null
}

/** 选文件夹保存 PNG;用户取消返回 null;成功返回完整文件路径。 */
export async function savePngToFolder(defaultName: string, data: Uint8Array): Promise<string | null> {
  return saveFileToFolder(defaultName, 'png', data)
}

/** 保存 DeepSeek key —— 兼容旧入口(等价于 setAiConfig('deepseek','deepseek-chat',key))。 */
export async function setAiKey(key: string): Promise<void> {
  const invoke = invoker()
  if (!invoke) throw new Error('仅桌面端可用')
  await invoke('set_ai_key', { key })
}

/** 查询是否已配置 key(空态引导用)。非桌面环境恒为 false。 */
export async function aiKeyIsSet(): Promise<boolean> {
  const invoke = invoker()
  if (!invoke) return false
  return (await invoke('ai_key_is_set')) as boolean
}

/** 当前 AI 配置:厂商 / 模型 / 自定义 base_url / 是否已配 key。 */
export interface AiConfig {
  provider: string
  model: string
  baseUrl: string
  configured: boolean
}

/** 读当前 AI 配置(设置页回显用)。非桌面环境给 DeepSeek 空态。 */
export async function getAiConfig(): Promise<AiConfig> {
  const invoke = invoker()
  if (!invoke) return { provider: 'deepseek', model: '', baseUrl: '', configured: false }
  // 后端返回元组 (provider, model, base_url, configured)。
  const [provider, model, baseUrl, configured] = (await invoke('get_ai_config')) as [
    string,
    string,
    string,
    boolean
  ]
  return { provider, model, baseUrl, configured }
}

/** 保存 AI 配置(选厂商 + 模型 + base_url + key)→ 后端写文件 + 重启 iris sidecar 生效。
 *  `apiKey` 留空 = 保留旧 key(只换厂商/模型);非空 = 覆盖。`baseUrl` 空 = 用厂商默认端点。 */
export async function setAiConfig(
  provider: string,
  model: string,
  baseUrl: string,
  apiKey: string
): Promise<void> {
  const invoke = invoker()
  if (!invoke) throw new Error('仅桌面端可用')
  // Tauri v2 默认把 camelCase 参数名转成 Rust snake_case(baseUrl → base_url、apiKey → api_key)。
  await invoke('set_ai_config', { provider, model, baseUrl, apiKey })
}

/** 机器指纹(Windows MachineGuid 的 SHA-256 hex)。 */
export async function getMachineFingerprint(): Promise<string> {
  const invoke = invoker()
  if (!invoke) throw new Error('仅桌面端可用')
  return (await invoke('get_machine_fingerprint')) as string
}

export interface DesktopLicenseState {
  shopName: string
  fingerprint: string
  trialStartedAt: string
  expiresAt: string | null
  lastSyncedAt: string | null
}

export async function readDesktopLicense(): Promise<DesktopLicenseState | null> {
  const invoke = invoker()
  if (!invoke) return null
  const raw = (await invoke('read_desktop_license')) as {
    shopName: string
    fingerprint: string
    trialStartedAt: string
    expiresAt: string | null
    lastSyncedAt: string | null
  } | null
  return raw
}

export async function writeDesktopLicense(license: DesktopLicenseState): Promise<void> {
  const invoke = invoker()
  if (!invoke) throw new Error('仅桌面端可用')
  await invoke('write_desktop_license', { license })
}
